import React, { useState } from 'react';
import { useLogin, useForgotPinMutation } from './queries';
import Modal from '../../components/common/Modal';

import Route_eye from '../../images/logo_route.svg';
import PoweredBy_Logo from './poweredBy.svg';

interface LoginProps {
  onLoginSuccess?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState(['', '', '', '']);
  // const [rememberMe, setRememberMe] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    mobile: '',
    pin: ''
  });

  const { mutate: login, isPending, isError, error } = useLogin();

  const [showForgotPinModal, setShowForgotPinModal] = useState(false);
  const [forgotPinPhone, setForgotPinPhone] = useState('');
  const [forgotPinUsername, setForgotPinUsername] = useState('');
  const [forgotPinError, setForgotPinError] = useState('');
  const [forgotPinSuccess, setForgotPinSuccess] = useState(false);

  const { mutate: sendForgotPin, isPending: isForgotPinPending } = useForgotPinMutation();

  const handleForgotPin = () => {
    setForgotPinPhone('');
    setForgotPinUsername('');
    setForgotPinError('');
    setForgotPinSuccess(false);
    setShowForgotPinModal(true);
  };

  const handleCloseForgotPin = () => {
    setShowForgotPinModal(false);
  };

  const handleSubmitForgotPin = () => {
    setForgotPinError('');

    if (forgotPinPhone.length !== 10 || !/^\d{10}$/.test(forgotPinPhone)) {
      setForgotPinError('Please enter a valid 10-digit phone number.');
      return;
    }
    const formattedPhone = `+91${forgotPinPhone}`;
    sendForgotPin(
      {
        subject: 'PIN Reset Request',
        body: formattedPhone,
        userName: forgotPinUsername.trim(),
      },
      {
        onSuccess: () => {
          setForgotPinSuccess(true);
        },
        onError: (err: any) => {
          setForgotPinError(
            err?.response?.data?.message || 'Failed to submit request. Please try again.'
          );
        },
      }
    );
  };

  const handlePinChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newPin = [...pin];
      newPin[index] = value;
      setPin(newPin);

      // Clear PIN validation error when user starts typing
      if (validationErrors.pin) {
        setValidationErrors({ ...validationErrors, pin: '' });
      }

      if (value && index < 3) {
        document.getElementById(`pin-${index + 1}`)?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (pin[index]) {
        const newPin = [...pin];
        newPin[index] = '';
        setPin(newPin);
      } else if (index > 0) {
        const newPin = [...pin];
        newPin[index - 1] = '';
        setPin(newPin);
        document.getElementById(`pin-${index - 1}`)?.focus();
      }
      e.preventDefault();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Reset validation errors
    const errors = { mobile: '', pin: '' };
    let hasErrors = false;

    // Validate mobile number
    if (!mobile.trim()) {
      errors.mobile = 'Phone number is required';
      hasErrors = true;
    }

    // Validate PIN
    if (pin.join('').length !== 4) {
      errors.pin = 'PIN is required';
      hasErrors = true;
    }

    setValidationErrors(errors);

    // If there are validation errors, don't submit
    if (hasErrors) {
      return;
    }

    login({
      phoneNumber: mobile,
      pin: pin.join(''),
    }, {
      onSuccess: () => onLoginSuccess?.(),
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src={Route_eye} alt="RouteEye Logo" className="w-6 h-6 sm:w-8 sm:h-8" />
              <span className="text-lg sm:text-xl font-bold text-gray-900">RouteEye</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 flex-col lg:flex-row">
        {/* Left Side Content */}
        <div className="flex flex-1 bg-gray-100 items-center justify-center p-6 sm:p-8 lg:p-12">
          <div className="max-w-xl w-full">
            <div className="flex items-center mb-4 lg:mb-6">
              <img
                src="/web_logo.png"
                alt="Staff Iris Logo"
                className="w-8 h-8 mr-3"
              />
              <span className="text-xl font-bold text-gray-900">Staff IRIS</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6 leading-tight">
              Monitor your field-staffs in real time.
            </h1>
            <p className="text-gray-600 text-base lg:text-lg leading-relaxed mb-6 lg:mb-8">
              <span className="font-bold text-gray-900">Staff IRIS</span> is a simple, ready-to-use field-staff monitoring and management system that seamlessly uses <span className="font-bold text-gray-900">GSM</span> and <span className="font-bold text-gray-900">GPS</span> modules to provide Real-time staff tracking data, and helps in optimizing and monitoring staff resources. Anomaly detection keeps on alert to take necessary measures.
            </p>
            <div className="space-y-4">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
                Stay connected and in control.
              </h2>
              <a
                href="https://routeeye.io/contact/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border-2 border-gray-900 text-gray-900 hover:bg-gray-100 px-6 py-3 rounded-md font-medium transition-colors inline-block"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 bg-gray-100 flex items-center justify-center px-3 py-6 sm:p-6 lg:p-8">
          <div className="w-full max-w-[calc(100%-1.5rem)] sm:max-w-md bg-white rounded-lg shadow-lg p-3 sm:p-6 lg:p-8 border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-6">
              {/* Mobile Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone No.
                </label>
                <input
                  type="tel"
                  value={mobile}
                  onChange={(e) => {
                    setMobile(e.target.value);
                    // Clear mobile validation error when user starts typing
                    if (validationErrors.mobile) {
                      setValidationErrors({ ...validationErrors, mobile: '' });
                    }
                  }}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-600 rounded-lg focus:outline-none text-sm sm:text-base"
                  placeholder="Type your phone number"
                  disabled={isPending}
                />
                {validationErrors.mobile && (
                  <p className="text-red-600 text-sm mt-1">{validationErrors.mobile}</p>
                )}
              </div>

              {/* PIN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PIN
                </label>
                <div className="flex gap-1.5 sm:gap-3 justify-center sm:justify-start">
                  {pin.map((digit, index) => (
                    <input
                      key={index}
                      id={`pin-${index}`}
                      type="password"
                      value={digit}
                      onChange={(e) => handlePinChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="flex-1 max-w-[60px] h-[60px] sm:w-16 sm:h-16 sm:flex-none lg:w-16 lg:h-16 text-center text-lg sm:text-xl font-semibold border-2 border-gray-600 rounded-lg focus:outline-none"
                      maxLength={1}
                      disabled={isPending}
                    />
                  ))}
                </div>
                {validationErrors.pin && (
                  <p className="text-red-600 text-sm mt-1">{validationErrors.pin}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                {/* <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Remember me</span>
                </label> */}
                <button
                  type="button"
                  onClick={handleForgotPin}
                  className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                >
                  Forgot Pin?
                </button>
              </div>

              {/* Error Message */}
              {isError && (
                <div className="text-red-600 text-xs sm:text-sm bg-red-50 border border-red-200 rounded-md p-2.5 sm:p-3">
                  {(error as any)?.response?.data?.message || (error as any)?.message || 'Login failed. Please try again.'}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isPending}
                className="w-full bg-[#F9E719] hover:bg-[#E5D617] text-black font-bold py-3 sm:py-4 px-4 rounded-lg transition-colors text-lg sm:text-xl"
              >
                {isPending ? 'Signing In...' : 'Sign in'}
              </button>

              {/* Need Help */}
              <div className="text-center">
                <a
                  href="https://routeeye.io/contact/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs sm:text-sm text-blue-600 hover:text-blue-500 font-medium"
                >
                  Need Help?
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Forgot PIN Modal */}
      <Modal
        isOpen={showForgotPinModal}
        onClose={handleCloseForgotPin}
        title="Forgot PIN"
        size="sm"
      >
        {forgotPinSuccess ? (
          <div className="space-y-4">
            <p className="text-green-700 bg-green-50 border border-green-200 rounded-md p-3 text-sm">
              Request submitted. An administrator will process your request shortly.
            </p>
            <button
              type="button"
              onClick={handleCloseForgotPin}
              className="w-full bg-[#F9E719] hover:bg-[#E5D617] text-black font-bold py-2.5 px-4 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Enter your registered phone number and username. An administrator will reset your PIN.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 border-2 border-r-0 border-gray-600 rounded-l-lg bg-gray-100 text-gray-600 text-sm">
                  +91
                </span>
                <input
                  type="tel"
                  value={forgotPinPhone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setForgotPinPhone(val);
                    setForgotPinError('');
                  }}
                  className="flex-1 px-3 py-2.5 border-2 border-gray-600 rounded-r-lg focus:outline-none text-sm"
                  placeholder="10-digit number"
                  maxLength={10}
                  disabled={isForgotPinPending}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={forgotPinUsername}
                onChange={(e) => {
                  setForgotPinUsername(e.target.value);
                  setForgotPinError('');
                }}
                className="w-full px-3 py-2.5 border-2 border-gray-600 rounded-lg focus:outline-none text-sm"
                placeholder="Enter your username"
                disabled={isForgotPinPending}
              />
            </div>
            {forgotPinError && (
              <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-2.5">
                {forgotPinError}
              </p>
            )}
            <button
              type="button"
              onClick={handleSubmitForgotPin}
              disabled={isForgotPinPending}
              className="w-full bg-[#F9E719] hover:bg-[#E5D617] text-black font-bold py-2.5 px-4 rounded-lg transition-colors"
            >
              {isForgotPinPending ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        )}
      </Modal>

      {/* Footer */}
      <footer className="bg-white text-gray-700 py-3 sm:py-6 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex flex-col lg:flex-row lg:justify-between items-start gap-4 lg:gap-10 mb-2 sm:mb-3">
            {/* Company info */}
            <div className="w-full lg:flex-1">
              <div className="flex items-center gap-2 mb-2">
                <img src={Route_eye} alt="RouteEye Logo" className="w-5 h-5 sm:w-6 sm:h-6" />
                <span className="text-base sm:text-lg font-bold text-gray-900">RouteEye</span>
              </div>
              <p className="text-xs sm:text-sm leading-relaxed text-gray-600 max-w-md">
                Streamline your real-time vehicle tracking with our leading SaaS platform RouteEye. Trusted by over 100+ happy customers.
              </p>
            </div>

            {/* Other sections container */}
            <div className="w-full lg:flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              {/* Resources */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Resources</h3>
                <ul className="space-y-1.5">
                  <li>
                    <a
                      href="https://routeeye.io/features/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Features
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://routeeye.io/resources/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Blogs
                    </a>
                  </li>
                </ul>
              </div>

              {/* Support */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Support</h3>
                <ul className="space-y-1.5">
                  <li>
                    <a
                      href="https://routeeye.io/support/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Help
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://routeeye.io/contact/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Contact
                    </a>
                  </li>
                </ul>
              </div>

              {/* Terms */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Terms</h3>
                <ul className="space-y-1.5">
                  <li>
                    <a
                      href="https://routeeye.io/terms/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Terms
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://routeeye.io/privacy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Privacy
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://routeeye.io/cancellation-refund-return/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Cancellation & Refund
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom section */}
          <div className="border-t border-gray-300 pt-3 sm:pt-4 flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-4">
            <div className="text-[10px] sm:text-xs text-gray-600 text-center sm:text-left">
              © Copyright 2025 • RouteEye • All rights reserved.
            </div>

            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
              <img src={PoweredBy_Logo} alt="Powered by logo" className="h-2.5 sm:h-3.5" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;
